/**
 * Photos on the mobile job view: take or pick a photo against the job, see
 * the job's own photos (removable) and, read-only, the photos taken of its
 * device(s) at check-in. Tapping a photo opens its full-size read link.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { IconCamera, IconPhoto, IconX } from '@tabler/icons-react';
import type { WorkItemPhotosResponse } from '@techgitter/contracts';

import { deleteJobPhoto, fetchWorkItemPhotos, uploadJobPhoto } from '../../services/jobPhotosApi';
import './JobPhotosSection.css';

export const JobPhotosSection: React.FC<{ jobGuid: string }> = ({ jobGuid }) => {
  const [photos, setPhotos] = useState<WorkItemPhotosResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // One tap on a thumbnail's corner must not delete it — ask first.
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const result = await fetchWorkItemPhotos(jobGuid, signal);
      if (!signal?.aborted) setPhotos(result);
    } catch (e) {
      if (!signal?.aborted) setError(e instanceof Error ? e.message : 'Failed to load photos');
    }
  }, [jobGuid]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    const all = Array.from(files);
    let done = 0;
    try {
      for (const file of all) {
        await uploadJobPhoto(jobGuid, file);
        done += 1;
      }
    } catch (e) {
      const reason = e instanceof Error ? e.message : 'Failed to upload photo';
      setError(all.length > 1 ? `${done} of ${all.length} uploaded. ${reason}` : reason);
    } finally {
      // Show whatever did upload, so a part-failed batch isn't re-sent whole.
      await load();
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (pickerRef.current) pickerRef.current.value = '';
    }
  };

  const handleRemove = async (photoId: string) => {
    setConfirmRemove(null);
    setError(null);
    try {
      await deleteJobPhoto(jobGuid, photoId);
      setPhotos((prev) => (prev ? { ...prev, jobPhotos: prev.jobPhotos.filter((p) => p.id !== photoId) } : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove photo');
    }
  };

  const open = (url: string | null) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <IconCamera size={18} />}
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Take photo'}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<IconPhoto size={18} />}
          onClick={() => pickerRef.current?.click()}
          disabled={uploading}
        >
          Choose
        </Button>
      </Stack>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={pickerRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {photos === null && !error && <CircularProgress size={20} />}

      {photos && photos.jobPhotos.length > 0 && (
        <div className="job-photos__grid">
          {photos.jobPhotos.map((p) => (
            <div key={p.id} className="job-photos__tile">
              <button type="button" className="job-photos__thumb" onClick={() => open(p.signedUrl)}>
                {p.signedUrl ? <img src={p.signedUrl} alt={p.caption || 'Job photo'} loading="lazy" /> : null}
              </button>
              <IconButton
                size="small"
                className="job-photos__remove"
                aria-label="Remove photo"
                onClick={() => setConfirmRemove(p.id)}
              >
                <IconX size={14} />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      {photos && photos.checkInPhotos.length > 0 && (
        <>
          <div className="pickup-muted job-photos__label">From check-in</div>
          <div className="job-photos__grid">
            {photos.checkInPhotos.map((p) => (
              <div key={p.id} className="job-photos__tile">
                <button type="button" className="job-photos__thumb" onClick={() => open(p.signedUrl)}>
                  {p.signedUrl ? (
                    <img src={p.signedUrl} alt={p.caption || p.shot || 'Check-in photo'} loading="lazy" />
                  ) : null}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {photos && photos.jobPhotos.length === 0 && photos.checkInPhotos.length === 0 && (
        <p className="pickup-muted">No photos yet.</p>
      )}

      <Dialog open={!!confirmRemove} onClose={() => setConfirmRemove(null)} fullWidth maxWidth="xs">
        <DialogTitle>Remove this photo from the job?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmRemove && handleRemove(confirmRemove)}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
